import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import {
  listOrganizations,
  getOrganization,
  createOrganization,
  updateOrganization,
  type Organization,
  type CreateOrganizationInput,
  type UpdateOrganizationInput,
} from '@/services/organization.service';
import {
  listProductsByOrganization,
  createProduct,
  updateProduct,
  deleteProduct,
  regenerateApiKey,
  pauseProduct,
  resumeProduct,
  type Product,
  type CreateProductInput,
  type UpdateProductInput,
} from '@/services/product.service';

export type { Organization, Product };

export const useOrganizationStore = defineStore('organization', () => {
  const currentOrganization = ref<Organization | null>(null);
  const organizations = ref<Organization[]>([]);
  const products = ref<Product[]>([]);
  const selectedProduct = ref<Product | null>(null);
  const loading = ref(false);
  const error = ref<string | null>(null);

  const hasOrganization = computed(() => !!currentOrganization.value);

  // Alias for currentProduct (uses selectedProduct internally)
  const currentProduct = computed({
    get: () => selectedProduct.value,
    set: (value: Product | null) => {
      selectedProduct.value = value;
    },
  });

  // Products filtered by current organization
  const currentOrganizationProducts = computed(() => {
    if (!currentOrganization.value) return [];
    return products.value.filter(
      (p) => p.organizationId === currentOrganization.value?._id
    );
  });

  function setCurrentProduct(product: Product | null) {
    selectedProduct.value = product;
    if (product) {
      localStorage.setItem('ow_selected_product_id', product._id);
    } else {
      localStorage.removeItem('ow_selected_product_id');
    }
  }

  // Organization actions
  async function fetchOrganizations(): Promise<void> {
    loading.value = true;
    error.value = null;
    try {
      organizations.value = await listOrganizations();
      if (organizations.value.length > 0 && !currentOrganization.value) {
        currentOrganization.value = organizations.value[0];
      }
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Failed to fetch organizations';
      throw e;
    } finally {
      loading.value = false;
    }
  }

  async function fetchOrganization(id: string): Promise<Organization> {
    loading.value = true;
    error.value = null;
    try {
      const org = await getOrganization(id);
      currentOrganization.value = org;
      const index = organizations.value.findIndex((o) => o._id === id);
      if (index >= 0) {
        organizations.value[index] = org;
      }
      return org;
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Failed to fetch organization';
      throw e;
    } finally {
      loading.value = false;
    }
  }

  async function addOrganization(input: CreateOrganizationInput): Promise<Organization> {
    loading.value = true;
    error.value = null;
    try {
      const org = await createOrganization(input);
      organizations.value.push(org);
      if (!currentOrganization.value) {
        currentOrganization.value = org;
      }
      return org;
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Failed to create organization';
      throw e;
    } finally {
      loading.value = false;
    }
  }

  async function editOrganization(
    id: string,
    input: UpdateOrganizationInput
  ): Promise<Organization> {
    loading.value = true;
    error.value = null;
    try {
      const org = await updateOrganization(id, input);
      const index = organizations.value.findIndex((o) => o._id === id);
      if (index >= 0) {
        organizations.value[index] = org;
      }
      if (currentOrganization.value?._id === id) {
        currentOrganization.value = org;
      }
      return org;
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Failed to update organization';
      throw e;
    } finally {
      loading.value = false;
    }
  }

  // Product actions
  async function fetchProducts(organizationId?: string): Promise<void> {
    const orgId = organizationId || currentOrganization.value?._id;
    if (!orgId) {
      throw new Error('No organization selected');
    }

    loading.value = true;
    error.value = null;
    try {
      products.value = await listProductsByOrganization(orgId);
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Failed to fetch products';
      throw e;
    } finally {
      loading.value = false;
    }
  }

  async function addProduct(input: CreateProductInput): Promise<Product> {
    loading.value = true;
    error.value = null;
    try {
      const product = await createProduct(input);
      products.value.push(product);
      return product;
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Failed to create product';
      throw e;
    } finally {
      loading.value = false;
    }
  }

  async function editProduct(id: string, input: UpdateProductInput): Promise<Product> {
    loading.value = true;
    error.value = null;
    try {
      const product = await updateProduct(id, input);
      const index = products.value.findIndex((p) => p._id === id);
      if (index >= 0) {
        products.value[index] = product;
      }
      if (selectedProduct.value?._id === id) {
        selectedProduct.value = product;
      }
      return product;
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Failed to update product';
      throw e;
    } finally {
      loading.value = false;
    }
  }

  async function removeProduct(id: string): Promise<void> {
    loading.value = true;
    error.value = null;
    try {
      await deleteProduct(id);
      products.value = products.value.filter((p) => p._id !== id);
      if (selectedProduct.value?._id === id) {
        selectedProduct.value = null;
      }
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Failed to delete product';
      throw e;
    } finally {
      loading.value = false;
    }
  }

  async function regenerateProductApiKey(id: string): Promise<Product> {
    loading.value = true;
    error.value = null;
    try {
      const product = await regenerateApiKey(id);
      const index = products.value.findIndex((p) => p._id === id);
      if (index >= 0) {
        products.value[index] = product;
      }
      if (selectedProduct.value?._id === id) {
        selectedProduct.value = product;
      }
      return product;
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Failed to regenerate API key';
      throw e;
    } finally {
      loading.value = false;
    }
  }

  async function toggleProductPause(id: string): Promise<Product> {
    const product = products.value.find((p) => p._id === id);
    if (!product) {
      throw new Error('Product not found');
    }

    loading.value = true;
    error.value = null;
    try {
      const updatedProduct = product.isPaused ? await resumeProduct(id) : await pauseProduct(id);
      const index = products.value.findIndex((p) => p._id === id);
      if (index >= 0) {
        products.value[index] = updatedProduct;
      }
      if (selectedProduct.value?._id === id) {
        selectedProduct.value = updatedProduct;
      }
      return updatedProduct;
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Failed to toggle product pause state';
      throw e;
    } finally {
      loading.value = false;
    }
  }

  function setCurrentOrganization(org: Organization | null) {
    currentOrganization.value = org;
    if (org) {
      localStorage.setItem('ow_selected_org_id', org._id);
    } else {
      localStorage.removeItem('ow_selected_org_id');
    }
  }

  function setSelectedProduct(product: Product | null) {
    selectedProduct.value = product;
    if (product) {
      localStorage.setItem('ow_selected_product_id', product._id);
    } else {
      localStorage.removeItem('ow_selected_product_id');
    }
  }

  function restoreSelection() {
    const savedOrgId = localStorage.getItem('ow_selected_org_id');
    const savedProductId = localStorage.getItem('ow_selected_product_id');

    if (savedOrgId && organizations.value.length > 0) {
      const org = organizations.value.find((o) => o._id === savedOrgId);
      if (org) {
        currentOrganization.value = org;
      }
    }

    if (savedProductId && products.value.length > 0) {
      const product = products.value.find((p) => p._id === savedProductId);
      if (product) {
        selectedProduct.value = product;
      }
    }
  }

  function clearError() {
    error.value = null;
  }

  return {
    // State
    currentOrganization,
    organizations,
    products,
    selectedProduct,
    currentProduct,
    loading,
    error,
    // Computed
    hasOrganization,
    currentOrganizationProducts,
    // Organization actions
    fetchOrganizations,
    fetchOrganization,
    addOrganization,
    editOrganization,
    setCurrentOrganization,
    // Product actions
    fetchProducts,
    addProduct,
    editProduct,
    removeProduct,
    regenerateProductApiKey,
    toggleProductPause,
    setSelectedProduct,
    setCurrentProduct,
    // Utils
    clearError,
    restoreSelection,
  };
});
